import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Icon from "../../../assets/icons/Icon";
import { useSelector } from "react-redux";

const AgGridContextMenu = forwardRef(
  ({ marketWatchTableRef, dispatch, uiActions }, ref) => {
    const { marketActiveTab } = useSelector((state) => state.market);

    const menuRef = useRef(null);

    const [contextMenu, setContextMenu] = useState({
      visible: false,
      x: 0,
      y: 0,
      rowData: null,
    });

    /* ---------------- CLOSE MENU ---------------- */
    useEffect(() => {
      const closeMenu = () => {
        setContextMenu((prev) =>
          prev.visible ? { ...prev, visible: false } : prev,
        );
      };

      window.addEventListener("click", closeMenu);
      window.addEventListener("scroll", closeMenu);

      return () => {
        window.removeEventListener("click", closeMenu);
        window.removeEventListener("scroll", closeMenu);
      };
    }, []);

    /* ---------------- EXPOSE API ---------------- */
    useImperativeHandle(ref, () => ({
      showContextMenu: ({ x, y, rowData }) => {
        const menu = menuRef.current;

        // menu is always mounted, so this is safe
        const menuWidth = menu?.offsetWidth || 150;
        const menuHeight = menu?.offsetHeight || 170;

        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        let posX = x;
        let posY = y;

        // right overflow
        if (x + menuWidth > screenWidth) {
          posX = screenWidth - menuWidth - 8;
        }

        // bottom overflow
        if (y + menuHeight > screenHeight) {
          posY = screenHeight - menuHeight - 8;
        }

        setContextMenu({
          visible: true,
          x: posX,
          y: posY,
          rowData,
        });
      },

      hideContextMenu: () =>
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          rowData: null,
        }),
    }));

    /* ---------------- MENU ACTIONS ---------------- */
    const handleMenuAction = (action, rowData) => {
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        rowData: null,
      });

      if (action === "delete") {
        marketWatchTableRef?.current?.handleKeyActions?.({
          row: rowData,
          action: "delete",
        });
      } else if (action === "info") {
        dispatch(
          uiActions.setCanvasOption({
            isOpen: true,
            rowData,
            name: "info",
          }),
        );
      } else if (action === "view chart") {
        dispatch(
          uiActions.setCanvasOption({
            isOpen: true,
            rowData,
            name: "chart",
          }),
        );
      } else if (action === "fav") {
        marketWatchTableRef?.current?.handleFavourite({ rowData });
      }
    };

    /* ---------------- RENDER ---------------- */
    return (
      <>
        {/* MENU IS ALWAYS MOUNTED (IMPORTANT) */}
        <ul
          ref={menuRef}
          className="custom-context-menu"
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            visibility: contextMenu.visible ? "visible" : "hidden",
            pointerEvents: contextMenu.visible ? "auto" : "none",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            listStyle: "none",
            padding: "6px 0",
            margin: 0,
            zIndex: 9999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            fontSize: "14px",
            minWidth: "140px",
          }}
        >
          <li
            style={{ padding: "6px 12px", cursor: "pointer" }}
            onClick={() => handleMenuAction("view chart", contextMenu.rowData)}
          >
            <Icon name="FaChartLine" /> &nbsp;Chart
          </li>

          <li
            style={{ padding: "6px 12px", cursor: "pointer" }}
            onClick={() => handleMenuAction("info", contextMenu.rowData)}
          >
            <Icon name="FaRegListAlt" /> &nbsp;Info
          </li>

          {marketActiveTab === "all" ? (
            <li
              style={{ padding: "6px 12px", cursor: "pointer" }}
              onClick={() => handleMenuAction("delete", contextMenu.rowData)}
            >
              <Icon name="MdDelete" /> &nbsp;Delete
            </li>
          ) : (
            <li
              style={{ padding: "6px 12px", cursor: "pointer" }}
              onClick={() => handleMenuAction("fav", contextMenu.rowData)}
            >
              <Icon name="FaRegStar" /> &nbsp;Favourite
            </li>
          )}
        </ul>
      </>
    );
  },
);

export default AgGridContextMenu;
